import { ServerRegistry } from './servers'
import { SocketManager } from './socket'
import { SortController } from './sort'
import { GraphDisplayManager } from './graph'
import { PercentageBar } from './percbar'
import { Tooltip, Caption, escapeHTML, formatNumber } from './util'

export class App {
  publicConfig

  constructor () {
    this.tooltip = new Tooltip()
    this.caption = new Caption()
    this.serverRegistry = new ServerRegistry(this)
    this.socketManager = new SocketManager(this)
    this.sortController = new SortController(this)
    this.graphDisplayManager = new GraphDisplayManager(this)
    this.percentageBar = new PercentageBar(this)

    this._taskIds = []
  }

  // Called once the DOM is ready and the app can begin setup
  init () {
    this._initMinehutHistoryModal()
    this.socketManager.createWebSocket()
  }

  setPageReady (isReady) {
    document.getElementById('push').style.display = isReady ? 'block' : 'none'
    document.getElementById('footer').style.display = isReady ? 'block' : 'none'
    document.getElementById('status-overlay').style.display = isReady ? 'none' : 'block'
  }

  setPublicConfig (publicConfig) {
    this.publicConfig = publicConfig

    this.serverRegistry.assignServers(publicConfig.servers)

    // Start repeating frontend tasks once it has received enough data to be considered active
    // This simplifies management logic at the cost of each task needing to safely handle empty data
    this.initTasks()
  }

  handleSyncComplete () {
    this.caption.hide()

    // Run a single bulk server sort instead of per-add event since there may be multiple
    this.sortController.show()
    this.percentageBar.redraw()

    // The data may not be there to correctly compute values, but run an attempt
    // Otherwise they will be updated by #initTasks
    this.updateGlobalStats()
  }

  initTasks () {
    this._taskIds.push(setInterval(this.sortController.sortServers, 5000))
    this._taskIds.push(setInterval(this.updatePingTimers, 100))
  }

  handleDisconnect () {
    this.tooltip.hide()

    // Reset individual tracker elements to flush any held data
    this.serverRegistry.reset()
    this.socketManager.reset()
    this.sortController.reset()
    this.graphDisplayManager.reset()
    this.percentageBar.reset()

    // Undefine publicConfig, resynced during the connection handshake
    this.publicConfig = undefined

    // Clear all task ids, if any
    this._taskIds.forEach(clearInterval)

    this._taskIds = []

    // Reset hidden values created by #updateGlobalStats
    this._lastTotalPlayerCount = undefined
    this._lastServerRegistrationCount = undefined

    this._lastMinehutUpdateTime = undefined

    // Reset modified DOM structures
    document.getElementById('stat_totalPlayers').innerText = 0
    this.networkDailyPeaks = {}
    document.getElementById('stat_networks').innerText = 0
    document.getElementById('network-totals').innerHTML = ''
    document.getElementById('minehut-leaderboard').innerHTML = ''

    this.setPageReady(false)
  }

  getTotalPlayerCount () {
    return this.serverRegistry.getServerRegistrations()
      .map(serverRegistration => serverRegistration.playerCount)
      .reduce((sum, current) => sum + current, 0)
  }

  getNetworkTotals () {
    const totals = {}
    for (const reg of this.serverRegistry.getServerRegistrations()) {
      const parts = reg.data.name.split(' - ')
      const network = parts[0] || reg.data.name
      if (!totals[network]) {
        totals[network] = { current: 0, peak: 0 }
      }
      totals[network].current += reg.playerCount
      if (reg.lastPeakData) {
        totals[network].peak += reg.lastPeakData.playerCount
      }
    }
    return totals
  }

  updateNetworkTotals () {
    const totals = this.getNetworkTotals()
    const container = document.getElementById('network-totals')

    for (const [network, data] of Object.entries(totals)) {
      const id = `network-total-${network.replace(/\s+/g, '-').toLowerCase()}`
      let el = document.getElementById(id)

      if (!el) {
        el = document.createElement('div')
        el.id = id
        el.className = 'network-total'
        el.innerHTML = `<div class="network-name">${network}</div><div class="network-count">0</div><div class="network-label">players</div><div class="network-peak"></div><div class="network-peak network-daily-peak"></div>`
        container.appendChild(el)
      }

      el.querySelector('.network-count').innerText = formatNumber(data.current)

      const peakEl = el.querySelector('.network-peak')
      if (data.peak > 0) {
        peakEl.innerText = `Peak: ${formatNumber(data.peak)}`
      }
      const dailyPeak = (this.networkDailyPeaks || {})[network]
      const dailyPeakEl = el.querySelector('.network-daily-peak')
      dailyPeakEl.innerText = `24h Peak: ${dailyPeak ? formatNumber(dailyPeak.playerCount) : '—'}`
      dailyPeakEl.title = dailyPeak ? 'Highest combined Minehut and Minekeep player count in the past 24 hours' : 'No successful pings in the past 24 hours'
    }
  }

  addServer = (serverId, payload, timestampPoints) => {
    // Even if the backend has never pinged the server, the frontend is promised a placeholder object.
    // result = undefined
    // error = defined with "Waiting" description
    // info = safely defined with configured data
    const serverRegistration = this.serverRegistry.createServerRegistration(serverId)

    serverRegistration.initServerStatus(payload)

    // playerCountHistory is only defined when the backend has previous ping data
    // undefined playerCountHistory means this is a placeholder ping generated by the backend
    if (typeof payload.playerCountHistory !== 'undefined') {
      // Push the historical data into the graph
      // This will trim and format the data so it is ready for the graph to render once init
      serverRegistration.addGraphPoints(payload.playerCountHistory, timestampPoints)

      // Set initial playerCount to the payload's value
      // This will always exist since it is explicitly generated by the backend
      // This is used for any post-add rendering of things like the percentageBar
      serverRegistration.playerCount = payload.playerCount
    }

    // Create the plot instance internally with the restructured and cleaned data
    serverRegistration.buildPlotInstance()

    // Handle the last known state (if any) as an incoming update
    // This triggers the main update pipeline and enables centralized update handling
    serverRegistration.updateServerStatus(payload, this.publicConfig.minecraftVersions)

    // Allow the ServerRegistration to bind any DOM events with app instance context
    serverRegistration.initEventListeners()
  }

  updateMinehutLeaderboard (servers) {
    const container = document.getElementById('minehut-leaderboard')

    if (!servers || servers.length === 0) {
      container.style.display = 'none'
      container.closest('.minehut-section').style.display = 'none'
      return
    }

    this._lastMinehutUpdateTime = Date.now()
    container.style.display = ''
    container.closest('.minehut-section').style.display = ''

    const INITIAL_SHOW = 5

    if (!document.getElementById('minehut-live-content')) {
      container.innerHTML = `<div class="minehut-header"><span class="minehut-title">Minehut Top Servers</span><span class="minehut-timer" id="minehut-timer"></span></div>
        <div class="minehut-search-wrap">
          <input id="minehut-search" class="minehut-search" type="search" placeholder="Search any Minehut server..." autocomplete="off" aria-label="Search Minehut servers">
          <div id="minehut-search-results" class="minehut-search-results"></div>
        </div>
        <div id="minehut-live-content"></div>`

      document.getElementById('minehut-search').addEventListener('input', event => {
        clearTimeout(this._minehutSearchTimer)
        const query = event.target.value.trim()
        this._minehutSearchTimer = setTimeout(() => this.searchMinehutServers(query), 250)
      })
    }

    const liveContent = document.getElementById('minehut-live-content')
    let html = '<div class="minehut-servers">'

    servers.forEach((server, i) => {
      const hiddenClass = i >= INITIAL_SHOW && !this._minehutLeaderboardExpanded ? ' minehut-server-hidden' : ''
      html += `<button type="button" class="minehut-server${hiddenClass}" data-minehut-index="${i}" aria-label="View history for ${escapeHTML(server.name)}">
        <span class="minehut-rank">#${i + 1}</span>
        <span class="minehut-name">${escapeHTML(server.name)}</span>
        <canvas class="minehut-sparkline" id="minehut-spark_${i}" width="80" height="28"></canvas>
        <span class="minehut-players">${formatNumber(server.playerCount)} players</span>
        <span class="minehut-server-timer" id="minehut-server-timer_${i}"></span>
      </button>`
    })

    html += '</div>'

    if (servers.length > INITIAL_SHOW && !this._minehutLeaderboardExpanded) {
      html += `<button type="button" class="minehut-see-more" id="minehut-see-more">See ${servers.length - INITIAL_SHOW} more servers</button>`
    }

    liveContent.innerHTML = html

    liveContent.querySelectorAll('.minehut-server').forEach((row, i) => {
      row.addEventListener('click', () => this.showMinehutHistory(servers[i].name))
    })

    // Draw sparklines
    servers.forEach((server, i) => {
      if (server.history && server.history.length > 1) {
        this._drawSparkline(`minehut-spark_${i}`, server.history)
      }
    })

    // Bind see-more button
    const seeMoreBtn = document.getElementById('minehut-see-more')
    if (seeMoreBtn) {
      seeMoreBtn.addEventListener('click', () => {
        this._minehutLeaderboardExpanded = true
        const hidden = liveContent.querySelectorAll('.minehut-server-hidden')
        hidden.forEach(el => el.classList.remove('minehut-server-hidden'))
        seeMoreBtn.style.display = 'none'
      })
    }
  }

  async searchMinehutServers (query) {
    const results = document.getElementById('minehut-search-results')
    const requestId = (this._minehutSearchRequestId || 0) + 1
    this._minehutSearchRequestId = requestId

    if (!query) {
      results.innerHTML = ''
      results.style.display = 'none'
      return
    }

    results.style.display = 'block'
    results.innerText = 'Searching mhtrack...'

    try {
      const response = await fetch(`/api/mhtrack/servers?q=${encodeURIComponent(query)}`)
      if (!response.ok) throw new Error('Search request failed')
      const data = await response.json()
      if (requestId !== this._minehutSearchRequestId) return

      results.innerHTML = ''
      if (!data.servers || data.servers.length === 0) {
        results.innerText = 'No tracked Minehut servers found.'
        return
      }

      for (const server of data.servers) {
        const row = document.createElement('button')
        row.type = 'button'
        row.className = 'minehut-search-result'

        const identity = document.createElement('span')
        identity.className = 'minehut-search-identity'
        const name = document.createElement('strong')
        name.innerText = server.name
        const updated = document.createElement('small')
        updated.innerText = `Saved ${new Date(server.ts * 1000).toLocaleString()}`
        identity.append(name, updated)

        const stats = document.createElement('span')
        stats.className = 'minehut-search-stats'
        const players = document.createElement('strong')
        players.innerText = `${formatNumber(server.player_count)} players`
        const details = document.createElement('small')
        details.innerText = `#${server.rank} · ${formatNumber(server.peak_count || 0)} all-time peak`
        stats.append(players, details)

        row.append(identity, stats)
        row.addEventListener('click', () => this.showMinehutHistory(server.name))
        results.appendChild(row)
      }
    } catch (error) {
      if (requestId === this._minehutSearchRequestId) results.innerText = 'Search is temporarily unavailable.'
    }
  }

  _initMinehutHistoryModal () {
    const modal = document.getElementById('minehut-history-modal')
    const canvas = document.getElementById('minehut-history-chart')
    const tooltip = document.getElementById('minehut-history-tooltip')
    const close = () => {
      modal.style.display = 'none'
      modal.setAttribute('aria-hidden', 'true')
    }

    document.getElementById('minehut-history-close').addEventListener('click', close)
    modal.addEventListener('click', event => {
      if (event.target === modal) close()
    })
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') close()
    })

    canvas.addEventListener('mousemove', event => {
      const points = this._minehutHistoryPoints || []
      if (points.length < 2) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = (event.clientX - rect.left) * (canvas.width / rect.width)
      const plotLeft = 48
      const plotRight = canvas.width - 16
      const ratio = Math.max(0, Math.min(1, (canvasX - plotLeft) / (plotRight - plotLeft)))
      const targetTs = points[0].ts + ratio * (points[points.length - 1].ts - points[0].ts)
      let nearestIndex = 0

      for (let i = 1; i < points.length; i++) {
        if (Math.abs(points[i].ts - targetTs) < Math.abs(points[nearestIndex].ts - targetTs)) nearestIndex = i
      }

      const point = points[nearestIndex]
      const date = new Date(point.ts * 1000)
      tooltip.innerText = `${formatNumber(point.player_count)} players · ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      tooltip.style.display = 'block'
      tooltip.style.left = `${canvas.offsetLeft + (event.clientX - rect.left)}px`
      tooltip.style.top = `${canvas.offsetTop + (event.clientY - rect.top) - 42}px`
      this._drawMinehutHistory(points, nearestIndex)
    })

    canvas.addEventListener('mouseleave', () => {
      tooltip.style.display = 'none'
      this._drawMinehutHistory(this._minehutHistoryPoints || [])
    })
  }

  async showMinehutHistory (name) {
    const modal = document.getElementById('minehut-history-modal')
    const title = document.getElementById('minehut-history-title')
    const meta = document.getElementById('minehut-history-meta')
    modal.style.display = 'flex'
    modal.setAttribute('aria-hidden', 'false')
    title.innerText = `${name} — Past 24 Hours`
    meta.innerText = 'Loading history from mhtrack...'

    try {
      const response = await fetch(`/api/mhtrack/history?name=${encodeURIComponent(name)}`)
      if (!response.ok) throw new Error('History request failed')
      const data = await response.json()
      const dayAgo = Math.floor(Date.now() / 1000) - (24 * 60 * 60)
      const points = (data.history || []).filter(point => point.ts >= dayAgo)

      if (!data.server || points.length === 0) {
        meta.innerText = 'No player history was saved for this server in the past day.'
        this._drawMinehutHistory([])
        return
      }

      const peak = Math.max(...points.map(point => point.player_count))
      const latest = points[points.length - 1].player_count
      meta.innerText = `${formatNumber(latest)} latest · ${formatNumber(peak)} peak · ${points.length} saved samples`
      this._drawMinehutHistory(points)
    } catch (error) {
      meta.innerText = 'Could not load history from mhtrack. Please try again.'
      this._drawMinehutHistory([])
    }
  }

  _drawMinehutHistory (points, hoverIndex) {
    this._minehutHistoryPoints = points
    const canvas = document.getElementById('minehut-history-chart')
    const ctx = canvas.getContext('2d')
    const w = canvas.width
    const h = canvas.height
    const pad = { left: 48, right: 16, top: 18, bottom: 30 }
    ctx.clearRect(0, 0, w, h)

    if (points.length < 2) return

    const counts = points.map(point => point.player_count)
    const max = Math.max(1, ...counts)
    const minTs = points[0].ts
    const maxTs = points[points.length - 1].ts
    const tsRange = maxTs - minTs || 1
    const xFor = ts => pad.left + ((ts - minTs) / tsRange) * (w - pad.left - pad.right)
    const yFor = count => pad.top + (1 - count / max) * (h - pad.top - pad.bottom)

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)'
    ctx.fillStyle = '#c5c2c8'
    ctx.font = '12px Open Sans, sans-serif'
    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (i / 4) * (h - pad.top - pad.bottom)
      ctx.beginPath()
      ctx.moveTo(pad.left, y)
      ctx.lineTo(w - pad.right, y)
      ctx.stroke()
      ctx.fillText(formatNumber(Math.round(max * (1 - i / 4))), 4, y + 4)
    }

    ctx.beginPath()
    points.forEach((point, i) => {
      const x = xFor(point.ts)
      const y = yFor(point.player_count)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })
    ctx.strokeStyle = '#b8b2ff'
    ctx.lineWidth = 3
    ctx.stroke()

    if (Number.isInteger(hoverIndex) && points[hoverIndex]) {
      const point = points[hoverIndex]
      const x = xFor(point.ts)
      const y = yFor(point.player_count)
      ctx.beginPath()
      ctx.moveTo(x, pad.top)
      ctx.lineTo(x, h - pad.bottom)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.lineWidth = 1
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(x, y, 5, 0, Math.PI * 2)
      ctx.fillStyle = '#FFF'
      ctx.fill()
      ctx.strokeStyle = '#6c5ce7'
      ctx.lineWidth = 2
      ctx.stroke()
    }

    ctx.fillStyle = '#c5c2c8'
    ctx.textAlign = 'left'
    ctx.fillText(new Date(minTs * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), pad.left, h - 8)
    ctx.textAlign = 'right'
    ctx.fillText(new Date(maxTs * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), w - pad.right, h - 8)
    ctx.textAlign = 'left'
  }

  _drawSparkline (canvasId, history) {
    const canvas = document.getElementById(canvasId)
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const w = canvas.width
    const h = canvas.height
    const points = history

    const min = Math.min(...points)
    const max = Math.max(...points)
    const range = max - min || 1

    ctx.clearRect(0, 0, w, h)

    // Fill area under the line
    ctx.beginPath()
    ctx.moveTo(0, h)
    for (let i = 0; i < points.length; i++) {
      const x = (i / (points.length - 1)) * w
      const y = h - ((points[i] - min) / range) * (h - 4) - 2
      ctx.lineTo(x, y)
    }
    ctx.lineTo(w, h)
    ctx.closePath()
    ctx.fillStyle = 'rgba(108, 92, 231, 0.15)'
    ctx.fill()

    // Draw line
    ctx.beginPath()
    for (let i = 0; i < points.length; i++) {
      const x = (i / (points.length - 1)) * w
      const y = h - ((points[i] - min) / range) * (h - 4) - 2
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.strokeStyle = '#a29bfe'
    ctx.lineWidth = 1.5
    ctx.stroke()

    // Draw endpoint dot
    const lastX = w
    const lastY = h - ((points[points.length - 1] - min) / range) * (h - 4) - 2
    ctx.beginPath()
    ctx.arc(lastX, lastY, 2, 0, Math.PI * 2)
    ctx.fillStyle = '#a29bfe'
    ctx.fill()
  }

  updatePingTimers = () => {
    const now = Date.now()

    for (const reg of this.serverRegistry.getServerRegistrations()) {
      const el = document.getElementById(`ping-timer_${reg.serverId}`)
      if (el) {
        if (reg.lastSuccessfulPingTime) {
          const elapsed = ((now - reg.lastSuccessfulPingTime) / 1000).toFixed(1)
          el.innerText = `${elapsed}s since last ping`
        } else {
          el.innerText = 'Waiting for ping...'
        }
      }
    }

    if (this._lastMinehutUpdateTime) {
      const elapsed = ((now - this._lastMinehutUpdateTime) / 1000).toFixed(1)
      const el = document.getElementById('minehut-timer')
      if (el) {
        el.innerText = `${elapsed}s since last update`
      }
    }
  }

  updateGlobalStats = () => {
    // Only redraw when needed
    // These operations are relatively cheap, but the site already does too much rendering
    const totalPlayerCount = this.getTotalPlayerCount()

    if (totalPlayerCount !== this._lastTotalPlayerCount) {
      this._lastTotalPlayerCount = totalPlayerCount
      document.getElementById('stat_totalPlayers').innerText = formatNumber(totalPlayerCount)
    }

    // Only redraw when needed
    // These operations are relatively cheap, but the site already does too much rendering
    const serverRegistrationCount = this.serverRegistry.getServerRegistrations().length

    if (serverRegistrationCount !== this._lastServerRegistrationCount) {
      this._lastServerRegistrationCount = serverRegistrationCount
      document.getElementById('stat_networks').innerText = serverRegistrationCount
    }

    this.updateNetworkTotals()
  }
}
